// FEb 10 SingleSelectFieldComponent.jsx:
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, FormControl } from '@mui/material';
import CustomFieldWrapper from './CustomFieldWrapper';
import { useDropdownDependencies } from '../../contexts/DropdownDependenciesContext';
import { useMultiAnswerField } from '../../contexts/MultiAnswerFieldContext'; // ← NEW: plain options map (unfiltered)

import { useFormAnswers } from '../../contexts/FormAnswersContext'; // other text channel
import { applyTransformations } from '../../utils/transformationUtils'; // live caps/spacing like TextFieldComponent

const SingleSelectFieldComponent = ({
  fieldId,
  fieldMetadata = {},
  value: propValue = '',
  onChange,
  disabled = false,
  isSubmitting = false,
  isBuilderMode = false,
  setShowDropZones,
  variant,
  error,
  helperText,
  externalHelperText,
}) => {

  const {
    dynamicDropdownOptions,
    notifyDependentFields,
    loadDependenciesForField,   
    loadingMap,  
    error: fetchError,
  } = useDropdownDependencies();
//  console.log(`[SingleSelectFieldComponent] Render start for fieldId ${fieldId}. Current dynamicDropdownOptions:`, dynamicDropdownOptions);

  const { dropdownOptions } = useMultiAnswerField(); // ← NEW: read the plain options map as a fallback

  // ─────────────────────────────────────────────────────────────
  // "Other" runtime-only behavior
  // - selection remains optionId "__other__"
  // - typed text is stored separately in FormAnswersContext
  // ─────────────────────────────────────────────────────────────
  const OTHER_OPTION_ID = "__other__";
  const ufi = fieldMetadata?.ufi || fieldId;

  const { getOtherText, setOtherText, clearOtherText } = useFormAnswers() || {};
  const otherTextValue = (typeof getOtherText === 'function') ? getOtherText(ufi) : '';

  const allowOtherEnabled =
    fieldMetadata?.allowOther === true || fieldMetadata?.includeOther === true;

  // per-dropdown loading flag
  // IMPORTANT:
  // - We still track dependency loading for correctness
  // - But once the field already has renderable options (seeded local or managed),
  //   we should NOT flash a transient "Loading options..." message in the builder.
  const perUfiLoading =
    fieldMetadata.optionsSetUFI &&
    loadingMap[fieldMetadata.optionsSetUFI] === true;

  const [internalValue, setInternalValue] = useState(''); // Stores optionId for dependencies
  const [options, setOptions] = useState([]);

  // Enterprise-grade render policy:
  // - Only treat the field as "blocking/loading" when we have no renderable options yet
  // - If options already exist, render normally and let dependency refresh happen silently
  const shouldBlockRenderForOptions =
    perUfiLoading && (!Array.isArray(options) || options.length === 0);

  const isAutocomplete = options.length > 0; // Determine if Autocomplete should be used

  /* ────────────────  make sure this dropdown’s dependencies are fetched  ──────────────── */
  useEffect(() => {
    if (!fieldMetadata.optionsSetUFI) return;                       // nothing to fetch
    // if this UFI is not currently being fetched *and* we’ve never fetched it, kick it off
    if (loadingMap[fieldMetadata.optionsSetUFI] === undefined) {
      loadDependenciesForField(fieldMetadata.optionsSetUFI);
    }
  }, [fieldMetadata.optionsSetUFI, loadDependenciesForField, loadingMap]);
  /* ─────────────────────────────────────────────────────────────────────────── */

  useEffect(() => {
    // Log the current dynamicDropdownOptions and optionsSetUFI before processing
//    console.log(`[SingleSelectFieldComponent: useeffect] Checking dynamicDropdownOptions for fieldId ${fieldId}:`, dynamicDropdownOptions);
//    console.log(`[SingleSelectFieldComponent: useeffect] optionsSetUFI for fieldId ${fieldId}: ${fieldMetadata.optionsSetUFI}`);

    const setId = fieldMetadata.optionsSetUFI;

    if (!setId) {
      setOptions([]);
      console.warn(`[SingleSelectFieldComponent: useeffect] No options found or invalid structure for optionsSetUFI ${fieldMetadata.optionsSetUFI}.`);
      return;
    }

    // NEW: Fallback chain so first paint is never empty for a brand-new set:
    // 1) primary: dependency-resolved choices (with filtering/OR logic)
    const fromDeps  = dynamicDropdownOptions?.[setId];
    // 2) fallback: plain options (seeded by MultiAnswerFieldContext)
    const fromPlain = dropdownOptions?.[setId];
    // 3) last-resort: embedded defaults in metadata (created by getDefaultFieldMetadata)
    const embedded  = Array.isArray(fieldMetadata?.options?.options) ? fieldMetadata.options.options : [];

    const optionsToDisplay =
      (Array.isArray(fromDeps)  ? fromDeps  :
       Array.isArray(fromPlain) ? fromPlain :
                                  embedded);

    // ─────────────────────────────────────────────────────────────
    // Runtime-only: append "Other" (never persisted to dropdownOptions)
    // ─────────────────────────────────────────────────────────────
    let finalOptions = Array.isArray(optionsToDisplay) ? [...optionsToDisplay] : [];
    if (allowOtherEnabled) {
      const alreadyHasOther = finalOptions.some(o => o?.optionId === OTHER_OPTION_ID);
      if (!alreadyHasOther) {
        finalOptions.push({
          optionId: OTHER_OPTION_ID,
          label: "Other",
          value: OTHER_OPTION_ID,
          hint: "",
        });
      }
    }

    // Log the options we intend to set
//    console.log(`[SingleSelectFieldComponent: useeffect] Setting options for fieldId ${fieldId} with optionsToDisplay:`, optionsToDisplay);

    setOptions(finalOptions);
  }, [
    fieldMetadata.optionsSetUFI,
    fieldMetadata.options,
    dynamicDropdownOptions,
    dropdownOptions,
    fieldId,
    allowOtherEnabled,
    OTHER_OPTION_ID
  ]);

 /**
  * Keep local selection controlled by the canonical propValue (optionId).
  * We do NOT mirror the label here; Autocomplete renders labels from options.
  */
 useEffect(() => {
   if (propValue !== undefined && propValue !== internalValue) {
     setInternalValue(propValue || '');
   }
 }, [propValue, internalValue]);


 /**
  * Handles selection change and sends the triggeringOptionId to the parent for dependency handling.
  * Triggers cascading update for dependent dropdowns.
  * NOTE: CustomFieldWrapper normalizes Autocomplete to call us with `{ target: { value: optionId } }`.
  */
 const handleChange = useCallback((event) => {
//   console.log(`[SingleSelectFieldComponent: handleChange] Called with event:`, event);

   const selectedOptionId = event?.target?.value ?? '';

   // Handle clearing selection properly
   if (!selectedOptionId) {
//     console.log(`[SingleSelectFieldComponent: handleChange] Clearing selection for fieldId ${fieldId}`);

     setInternalValue(''); // Reset internal selection

     notifyDependentFields(fieldMetadata.optionsSetUFI, null); // Ensure dependencies reset
     onChange({ target: { value: '' } }); // Propagate cleared state upwards (single call)

     // 🆕 Clear stale "Other" text if the selection is cleared
     if (typeof clearOtherText === 'function') {
       try { clearOtherText(ufi); } catch (_) {}
     }

     return;
   }

   // Normal selection handling (optionId only)
   setInternalValue(selectedOptionId);
//   console.log(`[SingleSelectFieldComponent: handleChange] Derived optionId: ${selectedOptionId}`);

   // Single canonical write
   onChange({ target: { value: selectedOptionId } });

   // ─────────────────────────────────────────────────────────────
   // "Other" must NOT drive dependency filtering.
   // If user selects Other, reset dependent overrides to canonical options.
   // Also keep the typed otherText stored separately.
   // ─────────────────────────────────────────────────────────────
   if (selectedOptionId === OTHER_OPTION_ID) {
     notifyDependentFields(fieldMetadata.optionsSetUFI, null); // reset overrides
   } else {
     // Non-other selection → clear stale other text
     if (typeof clearOtherText === 'function') {
       try { clearOtherText(ufi); } catch (_) {}
     }

     // Trigger update for dependent dropdowns
//     console.log(`[SingleSelectFieldComponent: handleChange] Setting triggeringOptionId for dependency: ${selectedOptionId}`);
     notifyDependentFields(fieldMetadata.optionsSetUFI, selectedOptionId);
   }

   if (isBuilderMode) {
     setShowDropZones(false);
   }
 }, [
   isBuilderMode,
   setShowDropZones,
   notifyDependentFields,
   fieldMetadata.optionsSetUFI,
   onChange,
   fieldId,
   OTHER_OPTION_ID,
   clearOtherText,
   ufi
 ]);


  const handleBlur = useCallback(() => {
    if (isBuilderMode) {
      setShowDropZones(true);
    }
  }, [isBuilderMode, setShowDropZones]);

  // Prevent rendering only when this dropdown truly has no renderable options yet.
  // IMPORTANT:
  // - We do NOT show a transient "Loading options..." helper message anymore.
  // - If options already exist, we render immediately for a smoother builder UX.
  if (shouldBlockRenderForOptions) {
//  console.log(`[SingleSelectFieldComponent] Waiting for dropdown options for fieldId ${fieldId}`);
    return null;  // Prevent rendering until options exist for this UFI
  }

  return (
    <FormControl fullWidth disabled={disabled || isSubmitting}>
      <CustomFieldWrapper
        label={fieldMetadata.label || 'Select Option'}
        variant={variant}
        dropdownOptions={options}
        multiple={false}
        value={internalValue} // ← canonical optionId; Autocomplete will render label from options
        onChange={handleChange}
        onBlur={handleBlur}
        required={fieldMetadata.required}
        error={error}
        helperText={fetchError ? externalHelperText : helperText}
        placeholder={fieldMetadata.hint || 'Select an option'}
        disabled={disabled || isSubmitting}
      />

      {/* ─────────────────────────────────────────────────────────────
           Indented "Other" text input (theme-aware via CustomFieldWrapper)
          - Applies caps/spacing as user types (like TextFieldComponent)
          - Encodes later during submission (FormActionsContext)
         ───────────────────────────────────────────────────────────── */}
      {allowOtherEnabled && internalValue === OTHER_OPTION_ID && (
        <Box sx={{ ml: 2, mt: 1 }}>
          <CustomFieldWrapper
            label={fieldMetadata.otherHint || 'Please specify'}
            variant={variant}
            value={otherTextValue || ''}
            onChange={(e) => {
              const raw = e?.target?.value ?? '';
              let next = applyTransformations(raw, fieldMetadata.transformations || {});
              const max = fieldMetadata.otherMaxLength ?? fieldMetadata.maxLength ?? null;
              if (max && Number.isFinite(Number(max)) && next.length > Number(max)) {
                next = next.substring(0, Number(max));
              }
              if (typeof setOtherText === 'function') {
                setOtherText(ufi, next);
              }
            }}
            onBlur={handleBlur}
            required={fieldMetadata.required}
            error={error}
            helperText={fieldMetadata.otherHelperText || ''}
            externalHelperText={fieldMetadata.otherErrorText || externalHelperText}
            placeholder={fieldMetadata.otherHint || 'Please specify'}
            fullWidth
            InputProps={{
              inputProps: {
                maxLength: fieldMetadata.otherMaxLength ?? fieldMetadata.maxLength,
              },
            }}
            disabled={disabled || isSubmitting}
          />
        </Box>
      )}
    </FormControl>
  );

};

SingleSelectFieldComponent.propTypes = {
  fieldId: PropTypes.string.isRequired,
  fieldMetadata: PropTypes.shape({
    label: PropTypes.string,
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    externalError: PropTypes.bool,
    externalHelperText: PropTypes.string,
    helperText: PropTypes.string,
    hint: PropTypes.string,
    transformations: PropTypes.object,
    optionsSetUFI: PropTypes.string,
  }),
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isBuilderMode: PropTypes.bool,
  setShowDropZones: PropTypes.func,
  variant: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  externalHelperText: PropTypes.string,
};

export default SingleSelectFieldComponent;
